import { injectable, inject, InjectionToken } from "@launchtray/tsyringe-async";
import { SceneManager } from "../../../../../scene/SceneManager";
import { IAuthController } from "./IAuthController";
import { SignInSuccessfulPopupScene } from "../signinsuccessfulpopup/SignInSuccessfulPopupScene";
import { IContextResolver } from "../../../../composition/IContextResolver";
import { ILogger } from "../../../../logger/ILogger";
import { EmailAuthPopupScene } from "../emailauthpopup/EmailAuthPopupScene";
import { AsyncOperationNotification } from "../../../common/components/AsyncOperationNotification";
import { Viewable } from "../../../../../scene/viewable/Viewable";
import { ValidationFailedPopupScene } from "../validationfailedpopup/ValidationFailedPopupScene";
import { GitBookPage, Redirector } from "../../../../../tools/Redirector";
import { PasskeyAuthPopupScene } from "../passkeyLoginPopup/PasskeyLoginScenet";

@injectable()
export class AuthController implements IAuthController {
    private readonly contextResolver: IContextResolver;
    private readonly sceneManager: SceneManager;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly logger: ILogger;

    public constructor(
        @inject("IContextResolver") contextResolver: IContextResolver,
        sceneManager: SceneManager,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger
    ) {
        this.contextResolver = contextResolver;
        this.sceneManager = sceneManager;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
    }

    public openTermsOfUse(): void {
        Redirector.openGitBook(GitBookPage.TERMS_OF_USE);
    }

    public async showEmailAuthPopup(): Promise<boolean> {
        return await this.showScene(EmailAuthPopupScene);
    }

    public async showPasskeyAuthPopup(): Promise<boolean> {
        return await this.showScene(PasskeyAuthPopupScene);
    }

    public async showEmailVerificationPopup(email: string): Promise<boolean> {
        return await this.showSceneWithParams("EmailVerificationPopupSceneFactory", email);
    }

    public async showValidationFailedPopup(): Promise<void> {
        return await this.showScene(ValidationFailedPopupScene);
    }

    public async showSignInSuccessfulPopup(): Promise<boolean> {
        return await this.showScene(SignInSuccessfulPopupScene);
    }

    private async showSceneWithParams<TResult, TScene extends Viewable<TResult>>(factoryType: string, ...args: any[]): Promise<TResult> {
        try {
            this.asyncOperationNotification.show();
            const factory: (...args: any[]) => Promise<TScene> = await this.contextResolver.resolve(factoryType);
            this.asyncOperationNotification.hide();
            return await this.showViewable(await factory(args));
        } catch (e: unknown) {
            this.asyncOperationNotification.hide();
            this.logger.error(`[AuthController] Exception on resolve scene with params: `, e);
            return null;
        }
    }

    private async showScene<TResult, TScene extends Viewable<TResult>>(sceneType: InjectionToken<TScene>): Promise<TResult> {
        try {
            this.asyncOperationNotification.show();
            const scene = await this.contextResolver.resolve(sceneType);
            this.asyncOperationNotification.hide();
            return await this.showViewable(scene);
        } catch (e: unknown) {
            this.asyncOperationNotification.hide();
            this.logger.error(`[AuthController] Exception on resolve scene: `, e);
            return null;
        }
    }

    private async showViewable<TResult, TScene extends Viewable<TResult>>(viewable: TScene): Promise<TResult> {
        try {
            return await this.sceneManager.showViewableAsync(viewable);
        } catch (e: unknown) {
            this.logger.error(`[AuthController] Exception on show scene: `, e);
            return null;
        }
    }
}
